import type { ComponentType } from "react"
import { AstroCheckout } from "./components/astro-checkout"
import { AuraCheckout } from "./components/aura-checkout"
import { DefaultCheckout } from "./components/checkout-default"
import { CompatibilityCheckout } from "./components/compatibility-checkout"
import { SoulmateCheckout } from "./components/soulmate-checkout"

/**
 * Maps a funnel key to a checkout layout. Adding a new variant = one entry
 * here; no routing changes. Entries can be wrapped in `lazy()` individually to
 * code-split heavy/rare variants without changing this map's shape.
 *
 * `satisfies` keeps the literal keys inferred (so `CheckoutVariantKey` stays
 * exact) while still type-checking every value as a `ComponentType`.
 */
const checkoutVariantRegistry = {
  default: DefaultCheckout,
  compatibility: CompatibilityCheckout,
  "soulmate-sketch": SoulmateCheckout,
  "astro-map": AstroCheckout,
  "aura-scan": AuraCheckout,
} satisfies Record<string, ComponentType>

/** Every registered checkout variant key, as a literal union. */
export type CheckoutVariantKey = keyof typeof checkoutVariantRegistry

/** All registered checkout variant keys. Source of truth is the registry. */
export const CHECKOUT_VARIANT_KEYS = Object.keys(
  checkoutVariantRegistry,
) as CheckoutVariantKey[]

/** Narrow an arbitrary param value to a known checkout variant key. */
export function isCheckoutVariant(key: string): key is CheckoutVariantKey {
  return key in checkoutVariantRegistry
}

/** Checkout layout used when the funnel names none, or names an unknown one. */
export const DEFAULT_CHECKOUT_VARIANT: CheckoutVariantKey = "default"

/**
 * Quiz → checkout fallback: when the funnel names no `checkout_v` (no preset,
 * no URL param), imply the variant from the quiz so a quiz-only URL still lands
 * on its own paywall instead of the generic default. Narrow on purpose — only
 * funnels with a bespoke paywall are listed; everything else keeps the default.
 * An explicit `checkout_v` always wins over this. Maps `quiz_v` → `checkout_v`.
 */
const QUIZ_TO_CHECKOUT_FALLBACK: Record<string, CheckoutVariantKey> = {
  compatibility: "compatibility",
  "soulmate-sketch": "soulmate-sketch",
  "astro-map": "astro-map",
  "aura-scan": "aura-scan",
}

/** Param key carrying the quiz selector — mirrors quiz-registry's `quiz_v`. */
const QUIZ_PARAM_KEY = "quiz_v"

/** Param key that carries the checkout variant selector. */
const VARIANT_PARAM_KEYS = ["checkout_v"] as const

/** The checkout variant requested by the funnel params, if any. */
export function getRequestedCheckoutVariant(
  params: Record<string, string | undefined>,
): string | null {
  for (const key of VARIANT_PARAM_KEYS) {
    const value = params[key]
    if (value && value.trim()) return value.trim()
  }
  return null
}

export interface ResolvedCheckout {
  /** Registry key of the resolved variant. */
  id: string
  Component: ComponentType
}

/**
 * Resolve which checkout layout to render from the effective funnel params:
 * an explicit `checkout_v` wins; otherwise the variant is implied from the
 * quiz (`QUIZ_TO_CHECKOUT_FALLBACK`); otherwise the default. Pure.
 */
export function resolveCheckoutVariant(
  params: Record<string, string | undefined>,
): ResolvedCheckout {
  const requested = getRequestedCheckoutVariant(params)
  if (requested && isCheckoutVariant(requested)) {
    return { id: requested, Component: checkoutVariantRegistry[requested] }
  }
  // No explicit checkout_v: imply it from the quiz so a quiz-only URL reaches
  // its own paywall. Unlisted quizzes fall through to the default.
  const quizKey = params[QUIZ_PARAM_KEY]?.trim()
  if (quizKey && quizKey in QUIZ_TO_CHECKOUT_FALLBACK) {
    const id = QUIZ_TO_CHECKOUT_FALLBACK[quizKey]
    return { id, Component: checkoutVariantRegistry[id] }
  }
  return {
    id: DEFAULT_CHECKOUT_VARIANT,
    Component: checkoutVariantRegistry[DEFAULT_CHECKOUT_VARIANT],
  }
}
